import { typo } from '@design-system/styles'
import { css } from '@emotion/react'
import clsx from 'clsx'
import { format } from 'date-fns'
import { observer } from 'mobx-react'
import { useMemo } from 'react'

import { _OldButton } from '@/component/atoms/Button/_OldButton'
import { Icon } from '@/component/atoms/Icon'
import { B2G_CONTRACT_CONSULT_URL } from '@/constant/contract'
import { studentController } from '@/controller/student.controller'
import { dataTrackingAdopter } from '@/page/Academy/AcademyManage/StudentManage/_utils/dataTrackingAdopter'
import commonRepo from '@/repository/common.repo'
import modalService from '@/service/modal.service'
import { localStorageService } from '@/service/storage.service'
import { openLink } from '@/utils/common'
import { _mixpanelUtils } from '@/utils/trackings/_mixpanel'

const UpcomingStudentContractPlanModal = ({
  onClick,
  totalActiveStudents,
}: {
  onClick?: () => void
  totalActiveStudents?: number
}) => {
  const currentStudentContractPlan = commonRepo.currentStudentContractPlan
  const nextStudentContractPlan = commonRepo.nextStudentContractPlans?.[0]

  const newValueStatus = useMemo(() => {
    if (!currentStudentContractPlan || !nextStudentContractPlan) return ''

    if (!currentStudentContractPlan.contractValue && !nextStudentContractPlan.contractValue)
      return ''
    if (currentStudentContractPlan.contractValue === nextStudentContractPlan.contractValue)
      return ''

    if (currentStudentContractPlan.contractValue === null) return 'decrease'
    if (nextStudentContractPlan.contractValue === null) return 'increase'

    if (currentStudentContractPlan.contractValue > nextStudentContractPlan.contractValue) {
      return 'decrease'
    }

    if (currentStudentContractPlan.contractValue < nextStudentContractPlan.contractValue) {
      return 'increase'
    }
    return ''
  }, [currentStudentContractPlan, nextStudentContractPlan])

  if (!currentStudentContractPlan || !nextStudentContractPlan) return <></>
  return (
    <section css={updateContractModalStyle}>
      <header className="header">
        <Icon name="alert_error_fill" size={36} />
        <h1>이용권 계약 변경 안내</h1>
      </header>
      <article className="content">
        <p className="content-text">
          이용권 계약이 변경될 예정입니다. <br />
          신규 이용권 계약 시작일부터, 초과한 이용권은 자동 회수됩니다. <br />
          이용권을 활성화할 학생을 미리 확인해주세요.
        </p>
        <ul className="content-list">
          <li className="content-list-item">
            <span className="content-list-item-title">현재 이용권 개수</span>
            <span className="content-list-item-value">
              {currentStudentContractPlan.contractValue
                ? `${currentStudentContractPlan.contractValue}개`
                : '무제한'}
            </span>
          </li>
          <li className="content-list-item">
            <span className="content-list-item-title">신규 이용권 개수</span>
            <span className={clsx('content-list-item-value new-value', newValueStatus)}>
              {nextStudentContractPlan.contractValue === null
                ? '무제한'
                : nextStudentContractPlan.contractValue + '개'}
            </span>
          </li>
          <li className="content-list-item">
            <span className="content-list-item-title">신규 이용권 계약 시작일</span>
            <span className="content-list-item-value">
              {format(nextStudentContractPlan.contractStartDate, 'yyyy년 M월 d일')}
            </span>
          </li>
        </ul>
      </article>
      <div className="button-group">
        <_OldButton
          width={120}
          theme="default"
          onClick={() => {
            if (!commonRepo.currentStudentContractPlan || !commonRepo.user?.teacherId) return
            const storedOption = localStorageService.getItem('UPDATE_CONTRACT_MODAL_OPTION')
            const existingTeacherIds = storedOption?.teacherIds || []
            const currentTeacherId = commonRepo.user.teacherId

            if (!currentTeacherId) return

            localStorageService.setItem('UPDATE_CONTRACT_MODAL_OPTION')({
              contractStartDate: commonRepo.currentStudentContractPlan.contractStartDate.getTime(),
              teacherIds: [...existingTeacherIds, currentTeacherId],
            })
            modalService.closeModal()
            onClick?.()
          }}
        >
          확인
        </_OldButton>
        <_OldButton
          width={120}
          theme="primary"
          onClick={async () => {
            modalService.closeModal()
            openLink(B2G_CONTRACT_CONSULT_URL)

            let _totalActiveStudents = 0

            if (!totalActiveStudents && totalActiveStudents !== 0) {
              const getTotalActiveStudents = async () => {
                _totalActiveStudents = await studentController.fetchActiveStudents()
              }
              await getTotalActiveStudents()
            } else {
              _totalActiveStudents = totalActiveStudents
            }

            _mixpanelUtils.uiTrack('학생이용권_상담신청', {
              학교_이름: commonRepo.academy?.name || '',
              신청_경로: '계약변경 안내 모달',
              이용권_사용_비율: dataTrackingAdopter.이용권_사용_비율(
                _totalActiveStudents,
                commonRepo.currentStudentContractPlan?.contractValue,
              ),
              이용권_잔여_기간: dataTrackingAdopter.이용권_잔여_기간(
                commonRepo.currentStudentContractPlan?.contractEndDate,
              ),
            })
          }}
        >
          상담 신청
        </_OldButton>
      </div>
    </section>
  )
}

export default observer(UpcomingStudentContractPlanModal)

const updateContractModalStyle = css`
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 32px 24px 24px;
  .header {
    display: flex;
    align-items: center;
    flex-direction: column;
    gap: 10px;
    color: var(--Colors-Gray-900);
    ${typo.heading05}
    font-weight: 700;
  }
  .content {
    margin: 8px 0 28px;
    color: var(--Colors-Gray-700);
    ${typo.caption01}
    text-align: center;
  }
  .content-text {
    margin-bottom: 20px;
  }
  .content-list {
    display: flex;
    width: 362px;
    padding: 20px 40px;
    flex-direction: column;
    gap: 8px;
    border-radius: 8px;
    background: var(--Colors-Gray-100);
  }
  .content-list-item {
    display: flex;
    justify-content: space-between;
  }
  .content-list-item-title {
    color: var(--Colors-Gray-700);
    ${typo.body02}
    font-weight: 500;
  }
  .content-list-item-value {
    ${typo.body02}
    color: var(--Colors-Gray-900);
    &.decrease {
      color: var(--Colors-Red-500);
      font-weight: 700;
    }
    &.increase {
      color: var(--Colors-Sky-400);
      font-weight: 700;
    }
  }
  .button-group {
    display: flex;
    gap: 8px;
    justify-content: center;
  }
`
